import ko from "knockout";
import _ from "lodash";
import ObservableModel from "util/ObservableModel";
import QuestionGroupModel from "models/composer/QuestionGroup";

export default ObservableModel.extend(
    {
        defaults: {
            organizationId: "",
            type: "evaluation",
            name: "",
            notes: "",
            version: 0,
            published: false,
            questionGroups: [{}], //Default to a single question group
            comments: "",
            //publishedVersions
            //totalPublishedVersions
        },
        modelProperties: {
            questionGroups: QuestionGroupModel,
        },
    },
    function evaluationModel_init(model) {
        const self = this;

        self.modelId = _.uniqueId("evaluation");
        let hasFakeId = false;
        if (!self.id) {
            self.id = self.modelId;
            hasFakeId = true;
        }

        model = model || {};
        if (model.publishedVersions) {
            self.publishedVersions = model.publishedVersions.entities;
            self.totalPublishedVersions = model.publishedVersions.total;
        } else {
            self.publishedVersions = [];
        }

        self.npsQuestionCount = function () {
            return 0;
        };

        self.maxQuestionCount = ko.observable();
        self.totalQuestionCount = ko.computed(function () {
            return self
                .questionGroups()
                .reduce(
                    (totalQuestions, group) =>
                        totalQuestions + group.questions().length,
                    0
                );
        });

        self.totalQuestionCount.extend({
            localizedValidator: {
                max: {
                    value: self.maxQuestionCount,
                    messageKey:
                        "composer.evaluations.editor.evaluation.questionLimitError",
                    messageParams: {
                        maxQuestionCount: self.maxQuestionCount,
                    },
                    onlyIf: function () {
                        return self.maxQuestionCount() > 0;
                    },
                },
            },
        });

        self.addQuestionGroup = function (data, index) {
            const newQuestionGroupModel = new QuestionGroupModel(data);
            self.questionGroups.splice(index, 0, newQuestionGroupModel);
            return newQuestionGroupModel;
        };

        self.questionMap = ko.computed(function () {
            return _.reduce(
                self.questionGroups(),
                function (accumulator, questionGroup, index) {
                    questionGroup.index(index);
                    return _.assign(accumulator, questionGroup.questionMap());
                },
                {}
            );
        });

        self.maxNameLength = ko.observable();
        self.name = self.name.extend({
            required: true,
            trimmed: null,
            localizedValidator: {
                maxLength: {
                    value: self.maxNameLength,
                    messageKey: "composer.surveys.editor.survey.nameTooLong",
                    messageParams: {
                        maxLength: self.maxNameLength,
                    },
                    onlyIf: function () {
                        return self.maxNameLength() > 0;
                    },
                },
            },
        });

        self.questionGroups.extend({ deferred: true });

        // Add Knockout-Validation functionality.
        ko.validation.group(self, {
            deep: true,
            live: true, //Properly validate observable arrays, see https://github.com/Knockout-Contrib/Knockout-Validation/pull/223
        });

        // Add an unwrap function to remove all unnecessary properties.
        // toIgnore - {object} OR {array}
        //   {object} - An object that maps which properties to ignore based on the type of the model.
        //     The toIgnore key pertaining to this model would be;
        //       'evaluation': ['properties', 'to', 'ignore']
        //   OR {array} an array of properties to ignore:
        //       ['properties', 'to', 'ignore']
        self.unwrap = function (toIgnore, options) {
            toIgnore = toIgnore || {};
            options = options || {};
            const typeName = self.type();
            let ignore = [
                "errors",
                "isAnyMessageShown",
                "isValid",
                "modelId",
                "unwrap",
                "version",
                "questionGroups",
                "questionMap",
                "addQuestionGroup",
                "publishedVersions",
                "totalPublishedVersions",
            ];
            if (options.ignoreFakeIds && hasFakeId) {
                ignore.push("id");
            }
            let passToKids = false;
            if (toIgnore instanceof Array) {
                // if toIgnore is an array
                ignore = ignore.concat(toIgnore || []);
            } else {
                // if toIgnore is an object mapping
                ignore = ignore.concat(toIgnore[typeName] || []);
                passToKids = true;
            }
            const data = ObservableModel.unwrap(self, ignore);
            data.questionGroups = _.map(
                self.questionGroups(),
                function (submodel) {
                    return submodel.unwrap(
                        passToKids ? toIgnore : undefined,
                        options
                    );
                }
            );
            return data;
        };
    }
);
